import { onDocumentReady } from "@theme/utilities";
import {
  getActiveAbraPromotion,
  isIdentifiedCustomer,
  getReturningCustomerPromoConfig,
  mirrorActivePromotion,
  hasMirroredPromotion,
  IDENTIFIED_STORAGE_KEY,
  ABRA_ACTIVE_PROMOTION_KEY,
} from "@theme/identified-customer-promo";

/**
 * Client-side promo-conditional content controller (PSD-146).
 *
 * Source of truth for `window.Theme.customer.promo` and the `<html>` promo-state
 * classes that the render-blocking CSS in `base.css` keys off of:
 *   - `html.promo-active`   → the returning-customer Abra promo is active (unlocked treatment)
 *   - `html.promo-pending`  → identified, awaiting Abra activation (skeleton window)
 *   - (neither)             → locked / default treatment
 *
 * The pre-paint inline script resolves the INITIAL state synchronously to avoid
 * FOUC/CLS. This module re-confirms it at runtime and handles in-session changes
 * (e.g. Alia signup), cross-tab sync, and Abra finishing activation after load.
 *
 * @fires promo:changed - CustomEvent with `{ active, id, source }` whenever the
 *   active/locked treatment flips. Other components can subscribe.
 */

const ACTIVE_CLASS = "promo-active";
const PENDING_CLASS = "promo-pending";
const PROMO_CHANGED_EVENT = "promo:changed";

/** Abra tags `<body>` with `abra--<PromoName>-activated` when a promo activates. */
const ABRA_ACTIVATED_CLASS_PATTERN = /^abra--(.+)-activated$/;

/**
 * Sections we can't add theme settings to (e.g. Replo) opt in via their "Custom CSS",
 * which only reaches descendants: `> div { --promo-visibility: active|inactive }`.
 * The marker lands on the section's first child div.
 */
const SECTION_MARKER_SELECTOR = '[id^="shopify-section-"] > div';
const VISIBILITY_PROPERTY = "--promo-visibility";

/** Same-tab fallback poll (no storage/DOM event fires when this tab writes localStorage). */
const POLL_INTERVAL_MS = 200;
const POLL_MAX_TICKS = 50; // ~10s, matches the Abra-ready window in identified-customer-promo.js

const ANALYTICS_MAX_TICKS = 20; // ~4s waiting for PostHog before giving up

const root = document.documentElement;

/**
 * @param {unknown} value
 * @returns {string}
 */
function slugify(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * @returns {string} The configured returning-customer promo slug, or "".
 */
function configuredSlug() {
  return getReturningCustomerPromoConfig()?.abralinkParam || "";
}

/**
 * Authoritative runtime signal — Abra's own per-promo body class.
 * @param {string} slug
 * @returns {boolean}
 */
function hasAbraActivatedBodyClass(slug) {
  if (!slug || !document.body) {
    return false;
  }

  const target = slugify(slug);
  return Array.from(document.body.classList).some((className) => {
    const match = className.match(ABRA_ACTIVATED_CLASS_PATTERN);
    return match != null && slugify(match[1]) === target;
  });
}

/**
 * @param {{ id?: string, title?: string, discountCodes?: string[] } | null} promotion
 * @param {string} slug
 * @returns {boolean}
 */
function promotionMatchesSlug(promotion, slug) {
  if (!promotion || !slug) return false;
  const target = slugify(slug);
  if (slugify(promotion.title) === target) return true;
  if (promotion.id === slug) return true;
  return (promotion.discountCodes || []).some(
    (code) => slugify(code) === target,
  );
}

/**
 * @returns {{ active: boolean, id: string | null, source: string | null }}
 */
function resolvePromoState() {
  const slug = configuredSlug();

  if (hasAbraActivatedBodyClass(slug)) {
    return { active: true, id: slug, source: "abra-body-class" };
  }

  const promotion = getActiveAbraPromotion();
  if (promotionMatchesSlug(promotion, slug)) {
    return { active: true, id: promotion.id || slug, source: "localstorage" };
  }

  // Theme-owned mirror — survives Abra removing `abra:active-promotion` during its boot, so the
  // `init` evaluate doesn't clobber the active state into the gap before Abra re-adds it.
  if (hasMirroredPromotion(slug)) {
    return { active: true, id: slug, source: "mirror" };
  }

  return { active: false, id: null, source: null };
}

/** @type {boolean | undefined} */
let previousActive;

/* ---- CSS-marker sections (Replo) ---- */

/**
 * @param {string} rawValue
 * @returns {"active" | "inactive" | null}
 */
function normalizeVisibility(rawValue) {
  const value = rawValue
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase();
  return value === "active" || value === "inactive" ? value : null;
}

/**
 * Reads the `--promo-visibility` Custom CSS marker on settings-less sections (e.g. Replo)
 * and toggles a collapse/reveal class on the section's child. These sections pin
 * `display: ... !important` inline on the child, which beats stylesheet `display` rules —
 * so the box is collapsed via size/overflow (which Replo does NOT pin inline), in base.css
 * and in the section's own Custom CSS. We avoid a first-paint flash by hiding the unlocked
 * side in Custom CSS (`> div:not(.promo-shown)`) rather than waiting on JS:
 *   - active   (unlocked): default-collapsed in Custom CSS; we add `.promo-shown` to reveal
 *                          when the promo is active.
 *   - inactive (locked):   visible by default; we add `.promo-collapsed` when active.
 * Skipped in the theme editor so these sections stay visible/editable there.
 * @param {boolean} active
 */
function syncCssMarkedSections(active) {
  if (window.Shopify?.designMode) return;

  document.querySelectorAll(SECTION_MARKER_SELECTOR).forEach((element) => {
    const visibility = normalizeVisibility(
      getComputedStyle(element).getPropertyValue(VISIBILITY_PROPERTY),
    );

    if (visibility === "active") {
      element.classList.toggle("promo-shown", active);
    } else if (visibility === "inactive") {
      element.classList.toggle("promo-collapsed", active);
    }
  });
}

/* ---- State application ---- */

/**
 * @param {boolean} active
 * @param {string | null} id
 * @param {"abra-body-class" | "localstorage" | "mirror" | null} source - how active was RESOLVED
 *   (drives the mirror decision — must not be the trigger).
 * @param {string} [trigger] - what caused this evaluation (init/poll/storage/…), for the event.
 */
function applyState(active, id, source, trigger) {
  const pending = !active && !!configuredSlug() && isIdentifiedCustomer();

  window.Theme.customer.promo = { active, id, pending, refresh: evaluate };

  // Persist genuine Abra confirmations into our own key so the pre-paint, Abra's removal window,
  // and future page loads resolve active without waiting. Not for source "mirror" (our own read —
  // refreshing from it would keep `mirroredAt` perpetually fresh and defeat the TTL).
  if (active && source !== "mirror") {
    mirrorActivePromotion(configuredSlug(), getActiveAbraPromotion());
  }

  root.classList.toggle(ACTIVE_CLASS, active);
  root.classList.toggle(PENDING_CLASS, pending);

  if (previousActive !== active) {
    previousActive = active;
    syncCssMarkedSections(active);
    document.dispatchEvent(
      new CustomEvent(PROMO_CHANGED_EVENT, {
        detail: { active, id, source, trigger },
      }),
    );
  }
}

/**
 * @param {string} [trigger] - what caused this evaluation (for the promo:changed event).
 * @returns {boolean} The resolved active state.
 */
function evaluate(trigger) {
  const state = resolvePromoState();
  applyState(state.active, state.id, state.source, trigger);
  return state.active;
}

/* ---- Runtime triggers ---- */

function observeAbraBodyClass() {
  if (!document.body) {
    return;
  }

  const observer = new MutationObserver(() => evaluate("abra-body-class"));
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

/**
 * Same-tab safety net for when Abra writes its localStorage state but no body-class
 * mutation is observed (timing). Stops once active or after the Abra-ready window.
 */
function pollUntilResolved() {
  let ticks = 0;
  const timer = setInterval(() => {
    ticks += 1;
    const active = evaluate("poll");

    if (active || ticks >= POLL_MAX_TICKS) {
      clearInterval(timer);

      // Promo never activated within the window → settle on the locked treatment.
      if (!active) {
        root.classList.remove(PENDING_CLASS);
        if (window.Theme.customer.promo) {
          window.Theme.customer.promo.pending = false;
        }
      }
    }
  }, POLL_INTERVAL_MS);
}

/**
 * @param {StorageEvent} event
 */
function onStorage(event) {
  const relevant =
    !event.key ||
    event.key === ABRA_ACTIVE_PROMOTION_KEY ||
    event.key === IDENTIFIED_STORAGE_KEY;

  if (relevant) {
    evaluate("storage");
  }
}

onDocumentReady(() => {
  if (!getReturningCustomerPromoConfig()?.enabled) {
    return;
  }

  evaluate("init");
  observeAbraBodyClass();

  // Cross-tab: a signup/activation in another tab updates this one.
  // (storage events fire in background tabs too, so no visibilitychange needed.)
  window.addEventListener("storage", onStorage);

  // Only poll while we actually expect activation (identified, not yet active).
  if (!window.Theme.customer.promo?.active && isIdentifiedCustomer()) {
    pollUntilResolved();
  }
});
